import { type Model, BuMNF, GQL, Utils } from "@binale-tech/shared";
import { Category } from "@domain/models/Category";
import { Debitor } from "@domain/models/Creditor";

type IstMNFConfig = {
    skipInVatExport: boolean;
    useUstMNFKonto: boolean;
    useMNFBalancing: boolean;
    switchUStBalancingSign?: boolean;
    /**
     * IST FE closing an M-NF Debitor through an account without FNR 10 and with BU scope KU (e.g. 1370 Durchlaufende
     * Posten). Such an account is no Geldkonto, so the offset is no Vereinnahmung (PO, 2026-11-04): the item books the
     * full brutto, no tax leg is added, and the USt stays parked on the M-NF account (SKR04 3816). Skonto and
     * Forderungsverluste are not KU scope and keep reducing the parked USt.
     */
    skipTaxLegs?: boolean;
};
const configOff: IstMNFConfig = Object.freeze({
    useUstMNFKonto: false,
    skipInVatExport: false,
    useMNFBalancing: false,
});
export class IstMNFUtils {
    static getLogic(
        productKey: GQL.IProductKey,
        yearConfig: Pick<GQL.ICompanyAccountingYear, "taxation" | "kontoExt" | "skr">,
        recordKonto: Model.IExtNum,
        itemKonto: Model.IExtNum,
        bu: Model.Bu,
    ): IstMNFConfig {
        if (yearConfig.taxation !== "IST") {
            return configOff;
        }
        if (!recordKonto?.num || !itemKonto?.num) {
            return configOff;
        }
        const isRecordKontoMNF = BuMNF.isMNFLogicForBu(yearConfig, bu, {
            extNum: recordKonto,
        });
        const isItemKontoMNF = BuMNF.isMNFLogicForBu(yearConfig, bu, {
            extNum: itemKonto,
        });
        if (!isItemKontoMNF && !isRecordKontoMNF) {
            return configOff;
        }
        if (Utils.PaymentUtils.isProductPaymentSource(productKey)) {
            return { useUstMNFKonto: isRecordKontoMNF, skipInVatExport: isRecordKontoMNF, useMNFBalancing: false };
        }
        if (productKey === GQL.IProductKey.Fe) {
            if (isRecordKontoMNF && !this.isFNR10(itemKonto) && this.isNoVatCategory(itemKonto)) {
                return { useUstMNFKonto: false, skipInVatExport: true, useMNFBalancing: false, skipTaxLegs: true };
            }
            return {
                useUstMNFKonto: isRecordKontoMNF,
                skipInVatExport: isRecordKontoMNF,
                useMNFBalancing: this.isFNR10(itemKonto),
            };
        }
        if (Utils.PaymentUtils.isProductPaymentRepresentation(productKey)) {
            return {
                useUstMNFKonto: isItemKontoMNF,
                skipInVatExport: false,
                useMNFBalancing: this.isFNR10(recordKonto),
                switchUStBalancingSign: this.isFNR10(recordKonto),
            };
        }
        return configOff;
    }

    /**
     * Whether an FE settles a Debitor through an account that does not release Ist-taxed USt: IST year, one side a
     * Debitor, the other a Sachkonto without FNR 10 and with BU scope KU (e.g. 1370). The form then shows the
     * "kein Geldkonto" hint (PO, 2026-11-04); the converter books it with `skipTaxLegs`.
     * @param taxation - Taxation of the record's year
     * @param productKey - Product of the record
     * @param account - The account the hint would sit on
     * @param counterAccount - The other side of the booking
     */
    static isNonGeldkontoSettlement(
        taxation: string | undefined,
        productKey: GQL.IProductKey,
        account: Model.IExtNum | undefined,
        counterAccount: Model.IExtNum | undefined,
    ): boolean {
        if (taxation !== GQL.ICompanyTaxation.Ist || productKey !== GQL.IProductKey.Fe) {
            return false;
        }
        return counterAccount instanceof Debitor && this.isNoVatCategory(account) && !this.isFNR10(account);
    }

    protected static isNoVatCategory(v: Model.IExtNum) {
        return v instanceof Category && v.buScope === "KU";
    }

    protected static isFNR10(v: Model.IExtNum) {
        return v instanceof Category && v.FNR === 10;
    }
}
